import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useT } from "@/context/ContentContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ImageIcon, Play, X } from "lucide-react";

const isVideoItem = (p) => {
  if (!p) return false;
  if (p.media_type === "video") return true;
  const url = (p.image_url || "").toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogv)(\?|$)/.test(url);
};

export default function Gallery() {
  const t = useT();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setPhotos(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="mx-auto max-w-7xl w-full px-6 py-16 flex-1">
        <div className="overline mb-3">{t("gallery.page.overline", "Team Gallery")}</div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">{t("gallery.page.title", "Moments & Trophies")}</h1>
        <p className="mt-4 text-white/65 max-w-2xl">{t("gallery.page.subtitle", "")}</p>

        {loading ? (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-square card-dark animate-pulse" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div className="card-dark mt-12 p-12 text-center text-white/60" data-testid="gallery-empty">
            <ImageIcon className="mx-auto mb-3 text-[#00E5FF]" />
            No photos yet — check back soon!
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {photos.map((p, i) => {
              const video = isVideoItem(p);
              const poster = p.thumbnail_url || "";
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p)}
                  className="group relative aspect-square overflow-hidden rounded-xl border border-white/10 hover:border-[#00E5FF]/40 transition-all"
                  data-testid={`gallery-photo-${p.id}`}
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  {video ? (
                    poster ? (
                      <img
                        src={poster}
                        alt={p.caption || "Team video"}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <video
                        src={p.image_url}
                        muted
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )
                  ) : (
                    <img
                      src={p.image_url}
                      alt={p.caption || "Team photo"}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/0 to-transparent" />
                  {video && (
                    <div className="absolute inset-0 grid place-items-center pointer-events-none">
                      <div className="h-14 w-14 rounded-full bg-black/60 backdrop-blur-md border border-white/30 grid place-items-center group-hover:scale-110 transition-transform">
                        <Play size={22} className="text-white translate-x-[1px]" fill="white" />
                      </div>
                    </div>
                  )}
                  {p.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                      <div className="text-xs text-white/90 leading-snug line-clamp-2">{p.caption}</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#050505] border-white/10 text-white max-w-4xl p-0 overflow-hidden">
          {active && (
            <div className="relative">
              <button
                onClick={() => setActive(null)}
                className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-black/60 backdrop-blur grid place-items-center hover:bg-black/80 text-white"
                data-testid="gallery-lightbox-close"
              >
                <X size={16} />
              </button>
              {isVideoItem(active) ? (
                <video
                  src={active.image_url}
                  poster={active.thumbnail_url || undefined}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[80vh] bg-black"
                  data-testid="gallery-lightbox-video"
                />
              ) : (
                <img
                  src={active.image_url}
                  alt={active.caption || "Team photo"}
                  className="w-full max-h-[80vh] object-contain bg-black"
                />
              )}
              {active.caption && (
                <div className="p-5 border-t border-white/10">
                  <div className="overline mb-1">Caption</div>
                  <div className="text-white/85 leading-relaxed">{active.caption}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
